import { useEffect, useMemo, useState } from "react";
import { Group, SegmentedControl, Select, Text, Button, Loader } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "../db";
import { providerRegistry, fetchModelsAndUpdateRegistry } from "../utils/providers";
import { config } from "../utils/config";

interface ModelSelectorProps {
  disabled?: boolean;
  fullWidth?: boolean;
}

export function ModelSelector({ disabled = false, fullWidth = true }: ModelSelectorProps) {
  const settings = useLiveQuery(async () => {
    return db.settings.where({ id: "general" }).first();
  });

  const [provider, setProvider] = useState<string | null>(null);
  const [model, setModel] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetchingModels, setFetchingModels] = useState(false);

  // Get all available providers
  const providers = useMemo(() => {
    return providerRegistry.getProvidersAsOptions();
  }, []);

  // Get available models for the selected provider
  // Force re-evaluate the memo whenever models might have changed
  const [modelRefreshTrigger, setModelRefreshTrigger] = useState(0);
  
  // Re-run when provider changes or when modelRefreshTrigger changes
  const availableModels = useMemo(() => {
    if (!provider) return [];
    return providerRegistry.getProviderModelsAsOptions(provider);
  }, [provider, modelRefreshTrigger]);
  
  // Check for updated models when component is mounted or becomes visible
  useEffect(() => {
    // Trigger a refresh of available models
    setModelRefreshTrigger(prev => prev + 1);
    
    // Set up an event listener for model updates
    const handleModelUpdate = (event: Event) => {
      console.log('Model update event received in ModelSelector');
      
      // Force an immediate refresh of models
      setModelRefreshTrigger(prev => prev + 1);
      
      // Get the event details
      const customEvent = event as CustomEvent;
      if (customEvent.detail && customEvent.detail.modelChanged) {
        // If the model was changed during the update, refresh the model selection
        // This ensures the UI stays in sync with the database
        const newSettings = db.settings.where({ id: "general" }).first();
        newSettings.then(settings => {
          if (settings && settings.model) {
            setModel(settings.model);
          }
        });
      }
    };
    
    // Listen for a custom event that will be dispatched when models are updated
    window.addEventListener('models-updated', handleModelUpdate);
    
    return () => {
      window.removeEventListener('models-updated', handleModelUpdate);
    };
  }, []);

  // Get the selected provider
  const selectedProvider = useMemo(() => {
    if (!provider) return null;
    return providerRegistry.get(provider);
  }, [provider]);

  // Initialize settings from database
  useEffect(() => {
    if (settings) {
      setProvider(settings.provider || config.defaultProvider);
      setModel(settings.model || config.defaultModel);
      
      // Check if the current model exists in the provider's model list
      if (settings.provider && settings.model) {
        const currentProvider = providerRegistry.get(settings.provider);
        if (currentProvider) {
          // Check if the selected model exists in the provider's models
          const modelExists = currentProvider.models.some(m => m.id === settings.model);
          
          if (!modelExists && currentProvider.models.length > 0) {
            // If the model doesn't exist but we have models, update to the first available model
            console.log(`Selected model ${settings.model} not found in provider's models. Updating to ${currentProvider.models[0].id}`);
            const newModel = currentProvider.models[0].id;
            
            // Update model in state and database
            setModel(newModel);
            db.settings.update("general", { model: newModel });
          }
        }
      }
    }
  }, [settings, modelRefreshTrigger]); // Also run when models are refreshed

  // Save model selection
  const handleModelChange = async (value: string | null) => {
    if (!value) return;
    
    setModel(value);
    setLoading(true);
    
    try {
      await db.settings.update("general", {
        model: value,
      });
      
      notifications.show({
        title: "Saved",
        message: "Model preference has been saved.",
        color: "green",
      });
    } catch (error: any) {
      const message = error.message || "Failed to save model preference.";
      notifications.show({
        title: "Error",
        color: "red",
        message,
      });
    } finally {
      setLoading(false);
    }
  };

  // Save provider selection
  const handleProviderChange = async (value: string | null) => {
    if (!value) return;
    
    setProvider(value);
    setLoading(true);
    
    try {
      // Get the first model for the new provider
      const providerModels = providerRegistry.getProviderModelsAsOptions(value);
      const firstModel = providerModels.length > 0 ? providerModels[0].value : null;
      
      // Update the model if it's different
      setModel(firstModel);
      
      await db.settings.update("general", {
        provider: value,
        model: firstModel,
      });
      
      notifications.show({
        title: "Saved",
        message: "Provider preference has been saved.",
        color: "green",
      });
    } catch (error: any) {
      const message = error.message || "Failed to save provider preference.";
      notifications.show({
        title: "Error",
        color: "red",
        message,
      });
    } finally {
      setLoading(false);
    }
  };

  // If there's only one provider, just show model selection
  if (providers.length <= 1 && provider === "openai") {
    return (
      <Group position="center" my={40}>
        <SegmentedControl
          value={model || undefined}
          onChange={handleModelChange}
          data={[
            { label: "GPT-3.5", value: "gpt-3.5-turbo" },
            { label: "GPT-4", value: "gpt-4" },
            { label: "GPT-4o", value: "gpt-4o" }
          ]}
          fullWidth={fullWidth}
          size="md"
          disabled={disabled || loading}
          sx={(theme) => ({
            [`@media (min-width: ${theme.breakpoints.md})`]: {
              width: fullWidth ? "100%" : "30%",
            },
          })}
        />
      </Group>
    );
  }

  // Function to fetch models from provider API
  const handleFetchModels = async () => {
    if (!provider || !selectedProvider) return;
    
    // Check if provider settings exist with an API key
    if (!settings?.providerSettings?.[provider]) {
      notifications.show({
        title: "Cannot Update",
        message: "Please set up the provider settings first.",
        color: "orange",
      });
      return;
    }
    
    setFetchingModels(true);
    try {
      // Use the centralized utility to fetch models and update the registry
      const models = await fetchModelsAndUpdateRegistry(provider);
      
      // Force a refresh of the models dropdown
      setProvider(prev => { 
        // This is a trick to force the useMemo for availableModels to recalculate
        // even though the actual provider value doesn't change
        const temp = null;
        setTimeout(() => setProvider(prev), 10); 
        return temp;
      });
      
      notifications.show({
        title: "Models Updated",
        message: `Successfully fetched ${models.length} models for ${selectedProvider.name}`,
        color: "green",
      });
    } catch (error: any) {
      notifications.show({
        title: "Error Fetching Models",
        message: error.message || "An error occurred while fetching models",
        color: "red",
      });
    } finally {
      setFetchingModels(false);
    }
  };

  // Show provider and model selection
  return (
    <Group position="center" my={40} sx={{ width: "100%" }}>
      <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Select
            label="AI Provider"
            placeholder="Select provider"
            value={provider || undefined}
            onChange={handleProviderChange}
            data={providers}
            disabled={disabled || loading || providers.length === 0}
            sx={{ minWidth: "200px", flexGrow: 1 }}
            clearable={false}
          />
          
          <Select
            label="Model"
            placeholder={fetchingModels ? "Fetching models..." : availableModels.length === 0 ? "No models available" : "Select model"}
            value={model || undefined}
            onChange={handleModelChange}
            data={availableModels}
            disabled={disabled || loading || !provider || availableModels.length === 0 || fetchingModels}
            sx={{ minWidth: "200px", flexGrow: 1 }}
            clearable={false}
            rightSection={fetchingModels ? <Loader size="xs" /> : null}
          />
        </div>
        
        {/* Removed the refresh button since models are fetched automatically when API key is saved */}
      </div>
    </Group>
  );
}
